/**
 * Floor + ceiling 3D generation — level-anchored horizontal prisms from a closed
 * boundary. Floors: top of assembly at level + offset, thickness DOWN (Revit
 * convention). Ceilings: underside at level + height offset, thickness UP.
 * Holes come free from extrudeOutlines' containment classification.
 */
import earcut from 'earcut'
import {
  isWoodCoreFloor, pathArcs, objectAnchorPoint, ceilingSlopePlane, ceilingSlopeRiseAt,
  type CeilingSlopePlane,
  type Path2D, type PhaseAreaEntry, type PhaseSplitEntry, type Pt2, type ResolvedElement,
} from '@aj/bim-kernel'
import { flattenPath } from '../../flatten'
import { siteSurfaceOfParams } from '@aj/bim-kernel'

/** a floor's display-system suffix: a concrete-cored floor rides Foundations; PLANTER
 *  SOIL (2026-10-05) rides Terrain — soil is site work, never structure. SITE SURFACES
 *  (2026-10-07): grass and water too; `|sitewater` also tells the 3D view to draw it
 *  as the see-through pool water */
function slabSystemSuffix(el: ResolvedElement): string {
  if (el.category !== 'floor' || isWoodCoreFloor(el)) return ''
  const site = siteSurfaceOfParams(el.typeParams)
  if (site === 'water') return '|sitewater'
  return site ? `|${site}` : '|foundation'
}
import type { ElementSolid } from '../wall/generate3d'
import { hasPhaseZones, phasePieces, phaseZones } from '../../phaseAreas'

/**
 * RECESSED VOID round (2026-08-24, Matt: "recessed fixtures need to cut a void
 * out of their host"): each hosted recessedCan's trim APERTURE as a hole ring in
 * the ceiling body — a true through-void via the extruder's containment
 * classification (the ceiling skin no longer covers the can; looking up you see
 * the housing, and a section through a can shows the real gap). Round trims cut
 * a 16-gon of the trim radius; square trims cut the rotated square. Only
 * recessedCan cuts — pendant/flushMount are SURFACE-mounted.
 */
function recessedApertures(el: ResolvedElement, hosted: ResolvedElement[]): Pt2[][] {
  if (el.category !== 'ceiling' || !hosted.length) return []
  const out: Pt2[][] = []
  for (const h of hosted) {
    if (h.category !== 'fixture' || h.typeParams.fixtureKind !== 'recessedCan') continue
    const anchor = objectAnchorPoint(h)
    if (!anchor) continue
    const w = (h.typeParams.widthFt as number) || 0.5
    const d = (h.typeParams.depthFt as number) || 0.5
    const r = Math.min(w, d) / 2
    if (r <= 0) continue
    if (h.typeParams.trimShape === 'square') {
      const rot = (((h.params.rotationDeg as number) || 0) * Math.PI) / 180
      const c = Math.cos(rot), s = Math.sin(rot)
      out.push(([[-r, -r], [r, -r], [r, r], [-r, r]] as Pt2[]).map(
        ([lx, ly]) => [anchor[0] + lx * c - ly * s, anchor[1] + lx * s + ly * c] as Pt2))
    } else {
      const pts: Pt2[] = []
      for (let i = 0; i < 16; i++) {
        const a = (Math.PI * 2 * i) / 16
        pts.push([anchor[0] + Math.cos(a) * r, anchor[1] + Math.sin(a) * r])
      }
      out.push(pts)
    }
  }
  return out
}

export function slabSolid(el: ResolvedElement, hosted: ResolvedElement[] = []): ElementSolid | null {
  const boundary = el.params.boundary as Path2D | null
  if (!boundary || !boundary.closed || boundary.nodes.length < 3) return null
  const t = el.assemblyThicknessFt
  if (t <= 0 || !el.level) return null
  const ringRaw = flattenPath(boundary)
  if (ringRaw.length < 3) return null
  // OUTLINE EDITOR holes (2026-09-28): the outer ring wound counter-clockwise, every
  // hole clockwise — plan's NonZero union then reads a hole as a void whichever way
  // the outline was first drawn
  const area2 = (r: Pt2[]) => r.reduce((a, p, i) => { const q = r[(i + 1) % r.length]; return a + p[0] * q[1] - q[0] * p[1] }, 0)
  const ring = area2(ringRaw) >= 0 ? ringRaw : [...ringRaw].reverse()
  const holes = ((el.params.boundaryHoles as Path2D[] | null) || [])
    .filter((h) => h && h.closed && h.nodes?.length >= 3)
    .map((h) => flattenPath(h))
    .filter((r) => r.length >= 3)
    .map((r) => (area2(r) <= 0 ? r : [...r].reverse()))
  const offset = (el.params.heightOffsetFt as number) || 0
  let zBase: number, zTop: number
  if (el.category === 'floor') {
    zTop = el.level.elevationFt + offset
    zBase = zTop - t
  } else {
    zBase = el.level.elevationFt + offset
    zTop = zBase + t
  }
  if (zTop <= zBase) return null
  // CEILING FACE MATERIAL fix (Round A, docs/bim-plan/18 §2.2): a ceiling's visible
  // face is its UNDERSIDE — the LAST layer. Ceilings stack top→bottom and a framed
  // ceiling's inserted joists land ABOVE the finish (ceilingCore.ts INSERT-ON-DECLARE),
  // so layers[0] painted the whole lid with the JOIST material once framed. Floors
  // keep layers[0] (their top layer IS the finish).
  const layers = el.assembly?.layers
  const finish = el.category === 'ceiling' && layers?.length
    ? layers[layers.length - 1]
    : layers?.[0]
  // CURVES round: the boundary IS the side face here (no offset math, unlike a wall's
  // centerline-derived faces) — its own arc edges are the smoothing candidates.
  const circles = pathArcs(boundary).map(({ arc }) => ({ c: arc.c, r: arc.r }))
  // RECESSED VOID round: aperture hole rings ride `outlines` (the 3D body + section
  // cuts get true voids); `fullOutlines` stays the bare boundary ring — the wall
  // slotted/unslotted convention, so the RCP's unioned outline (`fullOutlines ??
  // outlines`, plan.ts) never draws a second circle under each can's own glyph.
  const apertures = recessedApertures(el, hosted)
  // SLOPED CEILING round: one tilted plane from the slope-defining edge
  // SLOPED SLAB round (same day): floors too — the slope edge holds the floor's TOP at
  // its height (a floor's thickness runs down), so the body's underside base is the top
  // minus the plumb thickness
  const plane = ceilingSlopePlane(boundary)
  if (plane) {
    const zLoBase = el.category === 'floor' ? zTop - t * Math.sqrt(1 + plane.m * plane.m) : zBase
    return slopedCeilingSolid(el, plane, [ring, ...holes, ...apertures], apertures.length ? [ring, ...holes] : null,
      zLoBase, t, finish?.materialId ?? 'default')
  }
  return {
    elementId: el.id,
    category: el.category as 'floor' | 'ceiling',
    outlines: [ring, ...holes, ...apertures],
    ...(apertures.length ? { fullOutlines: [ring, ...holes] } : {}),
    zBase,
    zTop,
    materialId: finish?.materialId ?? 'default',
    // FRAMING SESSION fix round (Matt, 2026-08-13): a NON-wood floor is a
    // CONCRETE SLAB — its body classifies under the FOUNDATIONS system in the
    // viewport (with stems + footings), not Body & Finishes. The `|foundation`
    // key token is what sceneStore's systemOfChunk reads; grouping semantics
    // are unchanged (all such slabs carry it consistently).
    groupKey: `${el.category}|${el.levelId}|${el.type.assemblyId ?? 'none'}|${zBase.toFixed(4)}|${zTop.toFixed(4)}${slabSystemSuffix(el)}`,
    pieces: [],
    placements: [],
    openingInstances: [],
    ...(circles.length ? { circles } : {}),
  }
}

/**
 * PHASE SPLIT round (docs/MODELER.md): fan a single slab solid out into one solid
 * per phase region when the element carries `phaseSplits`. Left unsplit (v1 scope,
 * not a correctness bug) when the body carries recessed-can apertures
 * (`fullOutlines` present) — assigning each hole to its owning region needs the
 * same point-in-region test as the ring itself, and a phase-split ceiling with a
 * can right at the seam is rare enough to defer rather than risk a hole silently
 * landing in the wrong region's mesh.
 */
export function splitSlabByPhase(
  body: ElementSolid | null,
  el: ResolvedElement,
): ElementSolid | ElementSolid[] | null {
  if (!body) return body
  // PHASE AREAS (2026-10-30): the old straight cuts and the sketched areas resolve in
  // one place (phaseAreas.ts) — areas may run past the edge, and a slab with holes
  // splits too (the zones are intersected with every outline ring, NonZero)
  if (!hasPhaseZones(el.params) || body.fullOutlines || !body.outlines.length) return body
  const base = { phaseCreated: el.phaseCreated, phaseDemolished: el.phaseDemolished }
  const zones = phaseZones(body.outlines[0], el.params.phaseSplits as PhaseSplitEntry[] | null,
    el.params.phaseAreas as PhaseAreaEntry[] | null, base)
  const pieces = phasePieces(body.outlines, zones, base)
  if (!pieces || pieces.length <= 1) return body
  return pieces.map((piece) => ({
    ...body,
    outlines: piece.rings,
    // distinct per region — otherwise groupSolids/buildGroupChunk (and plan.ts's
    // own below-cut group-union pass) key on the UNCHANGED groupKey they'd
    // inherit from `body` and weld the differently-tagged regions right back into
    // one outline, silently erasing the split (the stem/footing union pass's own
    // "group by phase too" precedent — plan.ts).
    groupKey: `${body.groupKey}|phaseSplit:${piece.base ? 'base' : piece.key}`,
    phaseOverride: piece.tags,
    ...(piece.seams.length ? { phaseSeams: piece.seams } : {}),
  }))
}

/**
 * SLOPED CEILING round (2026-10-01): a ceiling whose boundary has a slope-defining
 * edge (kernel ceilingSlope.ts) — the underside is the tilted plane z = base + rise(p),
 * the body its assembly thickness above it measured square to the slope (plumb
 * thickness = t·√(1+m²), the roof convention), built as a closed mesh: earcut caps
 * (holes and recessed-can apertures kept) plus a vertical band on every ring edge.
 * meshOnly, so sections cut it through the generic mesh path; `outlines` still serve
 * the plan/RCP.
 */
function slopedCeilingSolid(
  el: ResolvedElement, plane: CeilingSlopePlane, rings: Pt2[][], fullOutlines: Pt2[][] | null,
  zBase: number, t: number, materialId: string,
): ElementSolid | null {
  const dv = t * Math.sqrt(1 + plane.m * plane.m)
  const zLo = (p: Pt2) => zBase + ceilingSlopeRiseAt(plane, p)
  const pos: number[] = [], nrm: number[] = [], idx: number[] = [], edges: number[] = []
  let vc = 0
  let zMin = Infinity, zMax = -Infinity
  const pushTri = (a: number[], b: number[], c: number[]) => {
    const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2]
    const vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2]
    const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx
    const len = Math.hypot(nx, ny, nz) || 1
    pos.push(...a, ...b, ...c)
    for (let k = 0; k < 3; k++) nrm.push(nx / len, ny / len, nz / len)
    idx.push(vc, vc + 1, vc + 2)
    vc += 3
    for (const p of [a, b, c]) { if (p[2] < zMin) zMin = p[2]; if (p[2] > zMax) zMax = p[2] }
  }
  // caps: one earcut over the outer ring + every hole, oriented per triangle
  const flat: number[] = []
  const holeIdx: number[] = []
  const all: Pt2[] = []
  rings.forEach((r, k) => {
    if (k > 0) holeIdx.push(all.length)
    for (const p of r) { flat.push(p[0], p[1]); all.push(p) }
  })
  const tris = earcut(flat, holeIdx.length ? holeIdx : undefined)
  if (!tris.length) return null
  for (let i = 0; i < tris.length; i += 3) {
    const [A, B, C] = [all[tris[i]], all[tris[i + 1]], all[tris[i + 2]]]
    const ccw = (B[0] - A[0]) * (C[1] - A[1]) - (B[1] - A[1]) * (C[0] - A[0]) > 0
    const lo = (p: Pt2) => [p[0], p[1], zLo(p)]
    const hi = (p: Pt2) => [p[0], p[1], zLo(p) + dv]
    // underside faces down, top faces up
    if (ccw) { pushTri(lo(A), lo(C), lo(B)); pushTri(hi(A), hi(B), hi(C)) }
    else { pushTri(lo(A), lo(B), lo(C)); pushTri(hi(A), hi(C), hi(B)) }
  }
  // side bands (outer ring CCW, holes CW → outward from the solid either way)
  for (const r of rings) {
    for (let i = 0; i < r.length; i++) {
      const P = r[i], Q = r[(i + 1) % r.length]
      const bP = [P[0], P[1], zLo(P)], bQ = [Q[0], Q[1], zLo(Q)]
      const tP = [P[0], P[1], zLo(P) + dv], tQ = [Q[0], Q[1], zLo(Q) + dv]
      pushTri(bP, bQ, tQ)
      pushTri(bP, tQ, tP)
      edges.push(...bP, ...bQ, ...tP, ...tQ, ...bP, ...tP)
    }
  }
  return {
    elementId: el.id,
    category: el.category as 'floor' | 'ceiling',
    outlines: rings,
    ...(fullOutlines ? { fullOutlines } : {}),
    zBase: zMin,
    zTop: zMax,
    materialId,
    groupKey: `${el.category}|${el.levelId}|${el.id}|sloped${slabSystemSuffix(el)}`,
    pieces: [],
    placements: [],
    openingInstances: [],
    mesh: {
      positions: new Float32Array(pos),
      normals: new Float32Array(nrm),
      indices: new Uint32Array(idx),
      edges,
    },
    meshOnly: true,
  }
}

/** SLOPED CEILING round: a sloped ceiling's underside / top heights at a plan point
 *  (for walls attached to it), or null for a flat ceiling */
export function slopedCeilingZ(el: ResolvedElement): { under: (p: Pt2) => number; top: (p: Pt2) => number } | null {
  if ((el.category !== 'ceiling' && el.category !== 'floor') || !el.level) return null
  const plane = ceilingSlopePlane(el.params.boundary as Path2D | null)
  if (!plane) return null
  const zb = el.level.elevationFt + ((el.params.heightOffsetFt as number) || 0)
  const dv = el.assemblyThicknessFt * Math.sqrt(1 + plane.m * plane.m)
  if (el.category === 'floor') {
    // SLOPED SLAB round: the floor's TOP is the tilted plane; the underside sits below it
    const top = (p: Pt2) => zb + ceilingSlopeRiseAt(plane, p)
    return { under: (p: Pt2) => top(p) - dv, top }
  }
  const under = (p: Pt2) => zb + ceilingSlopeRiseAt(plane, p)
  return { under, top: (p: Pt2) => under(p) + dv }
}
